import { useCallback } from "react";
import type { AccountPrice, Prices } from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";

export interface UsePriceDisplayParams {
  prices: Prices;
  formatOptions?: Intl.NumberFormatOptions;
  showRewards?: boolean;
}

export interface UsePriceDisplayResponse {
  priceText: string;
  rewardsText: string;
  combinedText: string;
  showRewardPrice: boolean;
  selectedAccountSpecific?: AccountPrice;
}

/**
 * TODO: Later on this should connect to a store and support multiple rewards accounts.
 * For now this just takes the first reward in account
 */
export const usePriceDisplay = ({ ...props }: UsePriceDisplayParams) => {
  const { getPriceDisplay } = useGetPriceDisplay();
  return getPriceDisplay(props);
};

export const useGetPriceDisplay = () => {
  const { formatRewardsCurrency, formatFiatCurrency } = useI18nContext();

  const getPriceDisplay = useCallback(
    ({
      prices: { accountSpecific, fiat },
      formatOptions = {},
      showRewards = true,
    }: UsePriceDisplayParams) => {
      const priceText = formatFiatCurrency(fiat, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
        ...formatOptions,
      });

      const [rewardsProgram] = Object.values(accountSpecific);
      const rewardsText = formatRewardsCurrency(rewardsProgram, formatOptions);
      const showRewardPrice =
        showRewards && rewardsText !== "" && rewardsText != null;

      const combinedText = `${priceText}${
        showRewardPrice ? ` / ${rewardsText}` : ""
      }`;

      return {
        priceText,
        rewardsText,
        combinedText,
        showRewardPrice,
        selectedAccountSpecific: rewardsProgram,
      };
    },
    [formatFiatCurrency, formatRewardsCurrency]
  );

  return { getPriceDisplay };
};
